import {Component, computed, input, output} from '@angular/core'
import {faChevronLeft} from '@fortawesome/pro-solid-svg-icons/faChevronLeft'
import {faChevronRight} from '@fortawesome/pro-solid-svg-icons/faChevronRight'
import {FaIconComponent} from '@fortawesome/angular-fontawesome'
import {NgClass} from '@angular/common'
import {I18nDirective} from '../i18n/i18n.directive'

const maxNumberOfVisiblePages = 5
// Number of pages from the end of the visible range at which the pagination will shift to show the next page in range.
const pageOffsetThreshold = 1

@Component({
  selector: 'cft-pagination',
  templateUrl: './pagination.component.html',
  styles: [
    `
      @reference '../../../styles/theme.css';

      :host {
        @apply inline-flex items-center justify-between;
      }
    `,
  ],
  imports: [FaIconComponent, NgClass, I18nDirective],
})
export class PaginationComponent {
  readonly pageOffset = input(0)
  readonly pageSize = input(20)
  readonly total = input.required<number>()
  readonly showPageNumbers = input(false)

  readonly pagingContext = computed<{
    currentPage: number
    pages: number[]
    numberOfPages: number
    pagesOutsideVisiblePageRange: boolean
    showPageNumbers: boolean
    total: number
    pageOffset: number
    pageSize: number
  }>(() => {
    const total = this.total()
    const pageSize = this.pageSize()
    const offset = this.pageOffset()

    const numberOfPages = Math.ceil(total / pageSize)
    const currentPage = Math.floor((offset / total) * numberOfPages) + 1

    let pages: number[] = []
    for (let i = 0; i < numberOfPages; i++) {
      pages.push(i + 1)
    }

    if (numberOfPages > maxNumberOfVisiblePages) {
      let start = Math.max(0, currentPage - maxNumberOfVisiblePages + pageOffsetThreshold)
      // Adjust start if we're at the end, to not show less than the maxNumberOfVisiblePages
      start = Math.min(start, numberOfPages - maxNumberOfVisiblePages)
      pages = pages.slice(start, start + maxNumberOfVisiblePages)
    }

    const lastVisiblePage = pages.slice().reverse()[0]
    const pagesOutsideVisiblePageRange = lastVisiblePage < numberOfPages
    const showPageNumbers = this.showPageNumbers()
    return {
      currentPage,
      pages,
      pagesOutsideVisiblePageRange,
      numberOfPages,
      showPageNumbers,
      total,
      pageOffset: offset,
      pageSize,
    }
  })

  nextClick = output()
  previousClick = output()
  pageClick = output<number>()

  readonly pageRightIcon = faChevronRight
  readonly pageLeftIcon = faChevronLeft

  go(pageNumber: number) {
    const pageSize = this.pageSize()
    this.pageClick.emit((pageNumber - 1) * pageSize)
  }

  next() {
    this.nextClick.emit()
  }

  previous() {
    this.previousClick.emit()
  }
}
